'use client';

import { c as _c } from "react/compiler-runtime";
import { pushCarouselEvent } from '@/globals/analytics/events/pushCarouselEvent';
import { ScrollButton } from '@/uiComposites/layout/scrollInline/ScrollButton';
import { ScrollInline, scrollInlineTranslations } from '@/uiComposites/layout/scrollInline/ScrollInline';
import { measureBefore, measureAfter, scrollInlineWrapper, scrollInlineNegativeInlinePadding } from '@/uiComposites/layout/scrollInline/ScrollInline.css';
import { useShowScrollButtons } from '@/uiComposites/layout/scrollInline/useShowScrollButtons';
import { classes } from '@/uiPrimitives/base/classes.helpers';
import { zIndex } from '@/uiPrimitives/layout/zIndex.css';
import { createContext, ReactNode, RefObject, use } from 'react';
import { carouselItemInlineSize, carouselSnap } from './MarkupCarousel.css';
export const scrollLeft = (scrollAreaElement: HTMLDivElement) => {
  const scrollDelta = Math.min(scrollAreaElement.clientWidth, carouselItemInlineSize);
  // scrollBy has janky behaviour on iOS Safari
  scrollAreaElement.scrollTo({
    left: scrollAreaElement.scrollLeft - scrollDelta,
    behavior: 'smooth'
  });
};
export const scrollRight = (scrollAreaElement: HTMLDivElement) => {
  const scrollDelta = Math.min(scrollAreaElement.clientWidth, carouselItemInlineSize);
  // scrollBy has janky behaviour on iOS Safari
  scrollAreaElement.scrollTo({
    left: scrollAreaElement.scrollLeft + scrollDelta,
    behavior: 'smooth'
  });
};
const CarouselContext = createContext<RefObject<HTMLDivElement | null> | undefined>(undefined);
const getSlideElements = (scrollAreaElement: HTMLDivElement) => {
  return Array.from(scrollAreaElement.querySelectorAll<HTMLLIElement>('li'));
};
const getNextSlideNumber = (scrollAreaElement: HTMLDivElement) => {
  const scrollAreaRect = scrollAreaElement.getBoundingClientRect();
  const slideElements = getSlideElements(scrollAreaElement);
  const nextSlideIndex = slideElements.findIndex(slideElement => {
    return slideElement.getBoundingClientRect().right > scrollAreaRect.right;
  });
  if (nextSlideIndex === -1) {
    return undefined;
  }
  return nextSlideIndex + 1;
};
const getPreviousSlideNumber = (scrollAreaElement: HTMLDivElement) => {
  const scrollAreaRect = scrollAreaElement.getBoundingClientRect();
  const slideElements = getSlideElements(scrollAreaElement);
  const previousSlideIndex = slideElements.findLastIndex(slideElement => {
    return slideElement.getBoundingClientRect().left < scrollAreaRect.left;
  });
  if (previousSlideIndex === -1) {
    return undefined;
  }
  return previousSlideIndex + 1;
};
export const ScrollInlineCarousel = t0 => {
  const $ = _c(36);
  const {
    children,
    className
  } = t0;
  const {
    measureBeforeRef,
    measureAfterRef,
    scrollAreaRef,
    showBeforeButton,
    showAfterButton
  } = useShowScrollButtons();
  let t1;
  if ($[0] !== className) {
    t1 = classes(className, scrollInlineWrapper, scrollInlineNegativeInlinePadding, zIndex({
      isolation: "isolate"
    }));
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = classes(carouselSnap);
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = classes(measureBefore);
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== measureBeforeRef) {
    t4 = <i className={t3} ref={measureBeforeRef} />;
    $[4] = measureBeforeRef;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = classes(measureAfter);
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== measureAfterRef) {
    t6 = <i className={t5} ref={measureAfterRef} />;
    $[7] = measureAfterRef;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const t7 = showBeforeButton === false && showAfterButton === false;
  const t8 = showBeforeButton && showAfterButton ? "both" : showBeforeButton ? "before" : showAfterButton ? "after" : undefined;
  let t9;
  if ($[9] !== children || $[10] !== scrollAreaRef || $[11] !== t4 || $[12] !== t6 || $[13] !== t7 || $[14] !== t8) {
    t9 = <ScrollInline scrollAreaClassName={t2} ref={scrollAreaRef} measureBeforeSlot={t4} measureAfterSlot={t6} scrollbarWidth="none" notOverflowing={t7} mask={t8}>{children}</ScrollInline>;
    $[9] = children;
    $[10] = scrollAreaRef;
    $[11] = t4;
    $[12] = t6;
    $[13] = t7;
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  const t10 = showBeforeButton ?? false;
  let t11;
  if ($[16] !== scrollAreaRef) {
    t11 = () => {
      const scrollAreaElement = scrollAreaRef.current;
      if (scrollAreaElement == null) {
        return;
      }
      scrollLeft(scrollAreaElement);
      const slideNumber = getPreviousSlideNumber(scrollAreaElement);
      if (slideNumber != null) {
        pushCarouselEvent({
          action: "previousClick",
          slideNumber
        });
      }
    };
    $[16] = scrollAreaRef;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] !== t10 || $[19] !== t11) {
    t12 = <ScrollButton.Item side="before" isShowing={t10} buttonLabel={scrollInlineTranslations["Scroll left in previous scroll area"]} onClick={t11} />;
    $[18] = t10;
    $[19] = t11;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  const t13 = showAfterButton ?? false;
  let t14;
  if ($[21] !== scrollAreaRef) {
    t14 = () => {
      const scrollAreaElement_0 = scrollAreaRef.current;
      if (scrollAreaElement_0 == null) {
        return;
      }
      scrollRight(scrollAreaElement_0);
      const slideNumber_0 = getNextSlideNumber(scrollAreaElement_0);
      if (slideNumber_0 != null) {
        pushCarouselEvent({
          action: "nextClick",
          slideNumber: slideNumber_0
        });
      }
    };
    $[21] = scrollAreaRef;
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  let t15;
  if ($[23] !== t13 || $[24] !== t14) {
    t15 = <ScrollButton.Item side="after" isShowing={t13} buttonLabel={scrollInlineTranslations["Scroll right in previous scroll area"]} onClick={t14} />;
    $[23] = t13;
    $[24] = t14;
    $[25] = t15;
  } else {
    t15 = $[25];
  }
  let t16;
  if ($[26] !== t12 || $[27] !== t15) {
    t16 = <ScrollButton.Shelf>{t12}{t15}</ScrollButton.Shelf>;
    $[26] = t12;
    $[27] = t15;
    $[28] = t16;
  } else {
    t16 = $[28];
  }
  let t17;
  if ($[29] !== t1 || $[30] !== t16 || $[31] !== t9) {
    t17 = <span className={t1}>{t9}{t16}</span>;
    $[29] = t1;
    $[30] = t16;
    $[31] = t9;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  let t18;
  if ($[33] !== scrollAreaRef || $[34] !== t17) {
    t18 = <CarouselContext value={scrollAreaRef}>{t17}</CarouselContext>;
    $[33] = scrollAreaRef;
    $[34] = t17;
    $[35] = t18;
  } else {
    t18 = $[35];
  }
  return t18;
};
export const SwipeButton = t0 => {
  const $ = _c(8);
  const {
    children,
    className
  } = t0;
  const scrollAreaRef = use(CarouselContext);
  let t1;
  if ($[0] !== className) {
    t1 = classes(className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== scrollAreaRef) {
    t2 = () => {
      if (scrollAreaRef?.current == null) {
        return;
      }
      scrollRight(scrollAreaRef.current);
      const slideNumber = getNextSlideNumber(scrollAreaRef.current);
      if (slideNumber != null) {
        pushCarouselEvent({
          action: "startClick",
          slideNumber
        });
      }
    };
    $[2] = scrollAreaRef;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== children || $[5] !== t1 || $[6] !== t2) {
    t3 = <button className={t1} onClick={t2}>{children}</button>;
    $[4] = children;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
};